// ═══════════════════════════════════════════════════════════════════
//  OVERRIDE EDIT MODEL (AB#806)
//
//  The pure half of creating an override from the availability week
//  (behind AmsAvailabilityWeekView): the editor's three states (view ↔
//  edit default ↔ override edit), which override buttons a schedule
//  offers and when, what each kind's form asks for and refuses, the
//  request it sends, the pending window drawn over the visible week,
//  and the resolution choice offered before an override that leaves
//  bookings behind is saved. Nothing here renders or fetches
//  (overrideEditModel.test.ts).
//
//  The form rules are the Schedule Overrides page's own
//  (overrideForm.ts fieldsFor / clientErrors / buildRequest): this
//  maps the week view's narrower form onto that one rather than
//  re-deciding them.
// ═══════════════════════════════════════════════════════════════════

import type { CalendarDate } from "@/app/booking-calendar/lib/siteTime"
import { addDays, mondayOf } from "@/app/booking-calendar/lib/siteTime"
import type { SlotPlanTarget } from "../availabilityApi"
import type {
  CreateScheduleOverrideRequest,
  EventTypeSlug,
  OverrideKind,
  ScheduleOverrideConflictProblem,
} from "../../schedule-overrides/scheduleOverridesApi"
import {
  buildRequest,
  clientErrors,
  DAY_END,
  DAY_START,
  KIND,
  NOTE_MAX_LENGTH,
  type OverrideFormState,
} from "../../schedule-overrides/lib/overrideForm"
import { cellKey, type GridFrame, isLocked } from "./editorModel"
import type { WeekColumn } from "./weekModel"

// ── The editor's states ─────────────────────────────────────────────

/** Which of its three states the editor is in. */
export type EditorMode = "view" | "editDefault" | "editOverride"

/** What moves it between them. */
export type EditorEvent = "editDefault" | "editOverride" | "close"

/** The editor's state, from its two edit slots: the default's draft and the override form. */
export function editorMode(draft: unknown, overrideEdit: unknown): EditorMode {
  if (draft !== null && draft !== undefined) return "editDefault"
  if (overrideEdit !== null && overrideEdit !== undefined) return "editOverride"
  return "view"
}

/**
 * The state after `event`. Either edit opens only from the view, and either closes back to it: the
 * default and an override are never edited at once, so a save of one cannot carry the other.
 */
export function nextMode(mode: EditorMode, event: EditorEvent): EditorMode {
  if (event === "close") return "view"
  return mode === "view" ? event : mode
}

// ── The buttons ─────────────────────────────────────────────────────

/** The override kinds the week view creates; no button makes a host-scoped event-type override. */
export type OverrideEditKind = "siteClosure" | "eventTypeOverride" | "unavailable" | "reassignment"

/** The buttons' and the form's titles. */
export const OVERRIDE_EDIT_LABEL: Record<OverrideEditKind, string> = {
  siteClosure: "Site closure",
  eventTypeOverride: "Event type override",
  unavailable: "Unavailable",
  reassignment: "Temporary site reassignment",
}

/** The AmsScheduleOverrideKind each creates. A reassignment is still stored as kind 0 (AB#805). */
export const OVERRIDE_EDIT_KIND: Record<OverrideEditKind, OverrideKind> = {
  siteClosure: KIND.siteClosure,
  eventTypeOverride: KIND.eventTypeOverride,
  unavailable: KIND.outOfOffice,
  reassignment: KIND.outreachAssignment,
}

/**
 * The buttons a schedule offers: the site's for a site schedule, the host's for a host schedule at a
 * main office. A host is managed from their main office — a temporary site reassignment is made only
 * from there (ScheduleOverridePlanner refuses one from an outreach site) — so a host schedule at an
 * outreach site offers none; see also `hostAtOutreach`.
 */
export function overrideKindsFor(target: Pick<SlotPlanTarget, "kind">, mainOffice: boolean): OverrideEditKind[] {
  if (target.kind === "site") return ["siteClosure", "eventTypeOverride"]
  return mainOffice ? ["unavailable", "reassignment"] : []
}

/** A host's schedule at an outreach site: read-only, with no override buttons and no default-schedule edit. */
export function hostAtOutreach(target: Pick<SlotPlanTarget, "kind">, mainOffice: boolean): boolean {
  return target.kind === "host" && !mainOffice
}

/** What decides whether the buttons show. */
export interface OverrideButtonGate {
  /** The dated week is on (AmsAvailabilityWeekView). */
  weekView: boolean
  /** appointments:manage. */
  canManage: boolean
  mode: EditorMode
  /** A slot-plan job for this schedule is still being applied. */
  jobRunning: boolean
  /** The week showing ended before this one: viewable, read-only. */
  pastWeek: boolean
}

/** Shown only in the view state, to a manager, on the dated week, with no job running and not on a past week. */
export function showOverrideButtons(gate: OverrideButtonGate): boolean {
  return gate.weekView && gate.canManage && gate.mode === "view" && !gate.jobRunning && !gate.pastWeek
}

// ── The form ────────────────────────────────────────────────────────

/** What the week view's override form holds. Date-times are Pacific, "yyyy-MM-ddTHH:mm". */
export interface OverrideEditForm {
  kind: OverrideEditKind
  start: string
  /** 11:59 PM stands for the end of the day, as on the Schedule Overrides page. */
  end: string
  /** Event type override only. */
  eventTypeSlug: EventTypeSlug | ""
  /** Reassignment only: the site they go to. */
  targetTeamSlug: string
  note: string
  /** Unavailable only: every site they host at, rather than this one (decision ams-ma0, off by default). */
  allSites: boolean
}

/** What the form is about besides itself: the selected schedule, and for a host, the email Cal knows them by. */
export interface OverrideEditContext {
  target: SlotPlanTarget
  /** Null when the host's email is not known (the Cal users have not loaded, or Cal has none). */
  hostEmail: string | null
}

/**
 * A fresh form for `kind`: the whole of the first day of the visible week that is not past — today
 * on this week, the Monday on a later one.
 */
export function newOverrideEdit(kind: OverrideEditKind, monday: CalendarDate, today: CalendarDate): OverrideEditForm {
  const first = mondayOf(monday) < today ? today : mondayOf(monday)
  return {
    kind,
    start: `${first}T${DAY_START}`,
    end: `${first}T${DAY_END}`,
    eventTypeSlug: "",
    targetTeamSlug: "",
    note: "",
    allSites: false,
  }
}

/** A change to the start that leaves the end behind it carries the end to the end of the new first day. */
export function withStart(form: OverrideEditForm, start: string): OverrideEditForm {
  const carried = `${start.slice(0, 10)}T${DAY_END}`
  return { ...form, start, end: start && form.end <= start && carried > start ? carried : form.end }
}

export type OverrideFieldRule = "required" | "optional" | "hidden"

/** Which fields a kind asks for beyond its start and end. */
export interface OverrideEditFields {
  eventType: boolean
  targetSite: boolean
  note: OverrideFieldRule
  allSites: boolean
}

export function overrideEditFields(kind: OverrideEditKind): OverrideEditFields {
  return {
    eventType: kind === "eventTypeOverride",
    targetSite: kind === "reassignment",
    note: kind === "unavailable" ? "required" : "optional",
    allSites: kind === "unavailable",
  }
}

/** The Schedule Overrides page's form for the same override, so its rules decide (fieldsFor, clientErrors, buildRequest). */
export function toOverrideFormState(form: OverrideEditForm, context: OverrideEditContext): OverrideFormState {
  const { target } = context
  const host = target.kind === "host"
  const fields = overrideEditFields(form.kind)
  return {
    kind: OVERRIDE_EDIT_KIND[form.kind],
    start: form.start,
    end: form.end,
    // "All of this host's sites" leaves the site out, which is the server's "every site".
    teamSlug: form.kind === "unavailable" && form.allSites ? "" : target.teamSlug,
    outreachTeamSlug: fields.targetSite ? form.targetTeamSlug : "",
    hostEmail: host ? context.hostEmail ?? "" : "",
    eventTypeSlug: fields.eventType ? form.eventTypeSlug : "",
    // An event-type override from the week view is always the whole site's.
    scope: "site",
    note: form.note,
  }
}

// The Schedule Overrides page's wording where the week view names the field differently.
const MESSAGE_FOR_WEEK_VIEW: Record<string, string> = {
  "Choose the outreach site.": "Choose the site they are reassigned to.",
  "Choose a host.": "This host's email is not known yet, so the override cannot be made. Try again in a moment.",
}

/** What stops the form being sent at all. The server's refusals come back through the preview. */
export function overrideEditErrors(form: OverrideEditForm, context: OverrideEditContext, today: CalendarDate): string[] {
  const errors = clientErrors(toOverrideFormState(form, context), today).map(message => MESSAGE_FOR_WEEK_VIEW[message] ?? message)
  if (form.kind === "reassignment" && form.targetTeamSlug && form.targetTeamSlug === context.target.teamSlug) {
    errors.push("Choose another site: they are already at this one.")
  }
  if (form.note.trim().length > NOTE_MAX_LENGTH) errors.push(`The note can be at most ${NOTE_MAX_LENGTH} characters.`)
  return errors
}

/** The create (and preview) request for the selected schedule, carrying only the fields this kind uses. */
export function buildOverrideRequest(form: OverrideEditForm, context: OverrideEditContext): CreateScheduleOverrideRequest | null {
  return buildRequest(toOverrideFormState(form, context))
}

// ── The reassignment's targets ──────────────────────────────────────

/** A site the host can be reassigned to. */
export interface ReassignmentTarget {
  teamSlug: string
  name: string
}

/**
 * Every other site (getTeams): once each, never the site the schedule is for, and never one Cal has
 * not slugged, sorted by name.
 *
 * Not only the host's own sites: saving a reassignment to a site they are not on yet adds them to its
 * team first, with no hours there of their own, and the override opens them for its window alone
 * (ScheduleOverrideService). The preview says so as a warning.
 */
export function reassignmentTargets(
  sites: readonly { slug?: string | null; name: string }[],
  fromTeamSlug: string,
): ReassignmentTarget[] {
  const bySlug = new Map<string, ReassignmentTarget>()
  for (const site of sites) {
    if (!site.slug || site.slug === fromTeamSlug || bySlug.has(site.slug)) continue
    bySlug.set(site.slug, { teamSlug: site.slug, name: site.name || site.slug })
  }
  return [...bySlug.values()].sort((a, b) => a.name.localeCompare(b.name))
}

// ── The pending window on the grid ──────────────────────────────────

/** "HH:mm" for a minute of the day; 1440 is "24:00", the end of the day. */
function clock(minutes: number): string {
  return `${String(Math.floor(minutes / 60)).padStart(2, "0")}:${String(minutes % 60).padStart(2, "0")}`
}

/** A form date-time with 11:59 PM read as the end of the day, as the server reads it. */
function windowEnd(end: string): string {
  return end.slice(11, 16) === DAY_END ? `${end.slice(0, 10)}T24:00` : end
}

/**
 * The cells of the visible week the pending override's window covers, by cellKey: every cell whose
 * slot overlaps [start, end). Breaks are left out — they stay unavailable whatever an override says.
 * Empty while the window cannot be read or runs backwards. Fixed-width Pacific date-times, so
 * comparing the strings compares the moments ("…T24:00" sorts after the day's last slot).
 */
export function pendingCells(
  form: Pick<OverrideEditForm, "start" | "end">,
  columns: readonly Pick<WeekColumn, "weekday" | "date">[],
  frame: GridFrame,
): Set<string> {
  const keys = new Set<string>()
  const pattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/
  if (!pattern.test(form.start) || !pattern.test(form.end)) return keys
  const start = form.start
  const end = windowEnd(form.end)
  if (end <= start) return keys

  for (const { weekday, date } of columns) {
    // Whole days outside the window are skipped without looking at their cells.
    if (`${date}T24:00` <= start || `${date}T00:00` >= end) continue
    for (const row of frame.rows) {
      if (isLocked(frame, weekday, row)) continue
      const cellStart = `${date}T${clock(row)}`
      const cellEnd = `${date}T${clock(row + frame.slotMinutes)}`
      if (cellStart < end && cellEnd > start) keys.add(cellKey(weekday, row))
    }
  }
  return keys
}

/** Whether any of the window falls in the visible week (Monday to Friday). */
export function windowTouchesWeek(form: Pick<OverrideEditForm, "start" | "end">, monday: CalendarDate): boolean {
  const first = mondayOf(monday)
  return form.start < `${addDays(first, 4)}T24:00` && windowEnd(form.end) > `${first}T00:00`
}

// ── Saving ──────────────────────────────────────────────────────────

/** The one-site-per-host refusal create answers 409 with (AB#805). */
export function isOverrideHostSiteConflict(data: unknown): data is ScheduleOverrideConflictProblem {
  return typeof data === "object" && data !== null && (data as { code?: unknown }).code === "host-site-conflict"
}

/**
 * What a refused create says, from the mutation's error: a 400's errors; a 409 "host-site-conflict"
 * as its message and then each overlap; another 409 is the applock's sentence; anything else generic.
 */
export function describeCreateError(error: unknown): string[] {
  const { status, data } = (error ?? {}) as { status?: number | string; data?: unknown }
  const errors = (data as { errors?: unknown } | null | undefined)?.errors

  if (status === 400 && Array.isArray(errors) && errors.length > 0) return errors.map(String)
  if (status === 409 && isOverrideHostSiteConflict(data)) {
    const overlaps = Array.isArray(data.conflicts)
      ? data.conflicts.map(c => String(c?.message ?? "")).filter(m => m.trim() !== "")
      : []
    const lead = typeof data.message === "string" && data.message.trim() !== ""
      ? data.message
      : "This host would be available at another site at the same time."
    return [lead, ...overlaps]
  }
  if (status === 409) {
    return [typeof data === "string" && data.trim() !== "" ? data : "Another override change is in progress. Try again in a moment."]
  }
  if (status === 403) return ["You do not have permission to create schedule overrides."]
  return ["Could not save the override. Try again in a moment."]
}

// ── The resolution choice ───────────────────────────────────────────

/**
 * What can be done about the bookings an override leaves behind. M13 has "proceed"; M14 adds
 * "automatic" (automatic reassignment, AB#810); "manual" (manual reschedule) is listed, disabled, until M15.
 */
export type ResolutionOptionId = "proceed" | "automatic" | "manual"

/**
 * One option of the resolution choice. The `id`/`label` pair is OverrideDetailDialog's choice shape;
 * an option that is offered but cannot be taken yet is `enabled: false` with the `reason`.
 */
export interface ResolutionOption {
  id: ResolutionOptionId
  label: string
  /** What taking it does, under the label. */
  description: string
  enabled: boolean
  /** Why it cannot be taken, when it is not enabled. */
  reason?: string
}

/** What an option the server decides says while its answer is still being read. */
export const CHECKING_OPTION_REASON = "Checking…"

/**
 * Every option in this build, in the order the choice lists them. Automatic reassignment and manual
 * reschedule are the server's to open (resolve/capabilities before Save, resolve/options after), so
 * both start disabled and stay so until it answers.
 */
export const RESOLUTION_OPTIONS: readonly ResolutionOption[] = [
  {
    id: "proceed",
    label: "Proceed without rescheduling",
    description:
      "Save the override and leave these appointments booked. The calendar flags them, and the override's detail keeps "
      + "listing them with their quick edits until each one is handled.",
    enabled: true,
  },
  {
    id: "automatic",
    label: "Automatic reassignment",
    description:
      "Save the override, then move each appointment to the earliest weekday past the 28-day booking window on which "
      + "the same host is free at the same time and site. You see the plan and confirm it before anything moves.",
    // Held until the server says it can run (withServerState): before Save GET …/resolve/capabilities,
    // after it GET …/{id}/resolve/options.
    enabled: false,
    reason: CHECKING_OPTION_REASON,
  },
  {
    id: "manual",
    label: "Manual reschedule",
    description: "Choose a new time for each appointment yourself.",
    // Until the server answers; it says "Available in a later update." until M15 opens it.
    enabled: false,
    reason: "Available in a later update.",
  },
]

/** The options the choice offers. */
export function resolutionOptions(options: readonly ResolutionOption[] = RESOLUTION_OPTIONS): ResolutionOption[] {
  return [...options]
}

/**
 * Whether Save asks first: only for a kind that affects bookings (closure, unavailable, reassignment
 * — `affects`, from NewOverrideDialog's affectsBookings) when the preview lists any.
 */
export function needsResolution(affects: boolean, bookings: readonly unknown[] | null | undefined): boolean {
  return affects && (bookings?.length ?? 0) > 0
}
